/**
 * Theme store — Zustand, persisted to localStorage.
 *
 * The initial theme is applied by an inline script in `index.html`
 * before first paint so users never see a flash. This store owns the
 * interactive toggle; setting it updates both `localStorage` and
 * the `class` on <html>.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type Theme = 'light' | 'dark';

interface ThemeState {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggle: () => void;
}

function applyToDom(theme: Theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  root.classList.toggle('light', theme === 'light');
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: resolveInitialTheme(),
      setTheme: (t) => {
        applyToDom(t);
        set({ theme: t });
      },
      toggle: () => get().setTheme(get().theme === 'dark' ? 'light' : 'dark'),
    }),
    {
      name: 'gi-engine.theme',
      storage: createJSONStorage(() => localStorage),
      // We only persist the value, not the setters. Re-apply on rehydrate
      // in case index.html's pre-paint script disagreed.
      onRehydrateStorage: () => (state) => {
        if (state) applyToDom(state.theme);
      },
      partialize: (state) => ({ theme: state.theme }),
    },
  ),
);

function resolveInitialTheme(): Theme {
  if (typeof document !== 'undefined' && document.documentElement.classList.contains('dark')) {
    return 'dark';
  }
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}
